import React, { useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  ArrowDropDown,
  CenterFocusStrongOutlined,
  Check,
  CheckCircleOutline,
  CloudOffOutlined,
  ContentCopy,
  DescriptionOutlined,
  ErrorOutline,
  FileDownloadOutlined,
  MoreVert,
  OpenInNew,
  PictureAsPdfOutlined,
  SaveOutlined,
  SlideshowOutlined,
  ViewAgendaOutlined,
  WidthNormal,
  WidthWide,
} from '@mui/icons-material';
import { useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import EnhancedRichTextEditor from './EnhancedRichTextEditor';
import documentService from '../services/documentService';
import { useActiveEditor } from '../context/ActiveEditorContext';
import { useAppActions } from '../context/AppContext';
import { quoteForInky, useEditorChrome, useWorkspaceLayout } from '../context/WorkspaceLayoutContext';
import { extractTrustComments } from '../utils/trustCitationUtils';
import DocumentSourcesChip from './documents/DocumentSourcesChip';
import useAutosave from './editor/useAutosave';
import { DocumentConflictError, notifyOpenerDocumentUpdated, saveDocumentContent } from './editor/documentPersistence';
import { loadTemplateField } from './editor/regenerateField';
import { buildAskAboutSectionPrompt, buildRegeneratePrompt, buildSourceFixPrompt, REGENERATE_GUIDANCE_CHIPS } from './editor/sectionHandoff';
import EntityBreadcrumbs from './layout/EntityBreadcrumbs';
import { EditorJobsButton, EditorNavButton, InkyReopenButton } from './layout/EditorChromeButtons';
import { formatShortcut } from '../hooks/useWorkspaceShortcuts';
import { PAPER_SIZE_IDS, PAPER_SIZES, readPaperSize, subscribePaperSize, writePaperSize } from '../utils/paperSize';
import {
  documentEditorPath,
  forgetRecentDocument,
  openInNewTab,
  projectPath,
  recordRecentDocument,
  registerUnsavedWorkFlusher,
} from '../utils/navigation';

/** Sticky document header height on one row (px); the measured height is used when it wraps. */
const HEADER_HEIGHT = 64;

/**
 * Whether editor HTML has no visible content (text, images, diagrams or tables).
 * @param {string} html - Editor HTML.
 * @returns {boolean} True when empty.
 */
const isEmptyHtml = (html) => {
  if (!html) return true;
  if (/<(img|table|hr)\b|data-mermaid-block|data-tpl-field|data-field-id/i.test(html)) return false;
  return html.replace(/<[^>]+>/g, '').replace(/&nbsp;| |​/g, '').trim() === '';
};

/**
 * Human message for a document that cannot be opened yet.
 * @param {object} data - Document row (with ai_requests).
 * @returns {string} Message.
 */
const emptyDocumentMessage = (data) => {
  const aiStatus = data?.ai_requests?.status;
  if (aiStatus === 'pending' || aiStatus === 'processing') {
    return 'This document is still being generated. Please wait a few moments and try again.';
  }
  if (aiStatus === 'failed') return 'Document generation failed. Please try regenerating the document.';
  return 'This document appears to have no content. It may need to be regenerated.';
};

/**
 * Whether a document is a slide deck (PowerPoint template output).
 * @param {object|null} data - Document row.
 * @param {string} html - Current HTML.
 * @returns {boolean} True for decks.
 */
const isDeckDocument = (data, html) => /<!--ppt_slides:/.test(html || data?.content || '');

/**
 * File-name-safe base name.
 * @param {string} title - Document title.
 * @returns {string} Base name.
 */
const fileBaseName = (title) => (title || 'Document').replace(/\.(docx|pdf|html|pptx)$/i, '').replace(/[\\/:*?"<>|]+/g, '-').trim() || 'Document';

/**
 * The single save status, a plain label next to the title (same as the template editor). The
 * tooltip (also on keyboard focus) gives the last saved time or the exact reason a save failed.
 * @param {{status: string, error?: string|null, lastSavedAt?: number|null, halted?: boolean}} props - Props.
 * @returns {JSX.Element|null} Label.
 */
export const SaveStatusChip = ({ status, error = null, lastSavedAt = null, halted = false }) => {
  const savedAt = lastSavedAt ? new Date(lastSavedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;
  const map = {
    saving: { label: 'Saving…', icon: <CircularProgress size={11} thickness={6} color="inherit" />, color: 'text.secondary', detail: null },
    saved: { label: 'Saved', icon: <CheckCircleOutline sx={{ fontSize: 14 }} />, color: 'var(--q-success-text)', detail: savedAt ? `Last saved at ${savedAt}` : 'All changes saved' },
    dirty: { label: 'Unsaved changes', icon: null, color: 'text.secondary', detail: 'Saving shortly' },
    error: {
      label: halted ? 'Not saved' : 'Save failed, retrying',
      icon: <ErrorOutline sx={{ fontSize: 14 }} />,
      color: 'var(--q-danger-text)',
      detail: `${error || 'The last save failed.'} ${halted ? `Autosave is paused: press ${formatShortcut('Mod+S')} to try again.` : 'Retrying automatically.'}`,
    },
    offline: { label: 'Offline, will retry', icon: <CloudOffOutlined sx={{ fontSize: 14 }} />, color: 'var(--q-warning-text)', detail: 'You are offline. Changes save when you reconnect.' },
    conflict: { label: 'Changed elsewhere', icon: <ErrorOutline sx={{ fontSize: 14 }} />, color: 'var(--q-danger-text)', detail: error || 'This document was changed elsewhere.' },
  };
  const entry = map[status];
  if (!entry) return null;
  const label = (
    <Box
      component="span"
      role="status"
      aria-live="polite"
      tabIndex={entry.detail ? 0 : undefined}
      data-testid="document-save-status"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flex: 'none', fontSize: 12.5, whiteSpace: 'nowrap', color: entry.color, borderRadius: '4px' }}
    >
      {entry.icon}
      {entry.label}
    </Box>
  );
  return entry.detail ? <Tooltip title={entry.detail} describeChild>{label}</Tooltip> : label;
};

/**
 * In-app document workspace (`/documents/:documentId/edit`, CONTRACTS-W2 §A). Renders inside
 * the app shell beside Inky. The editor is uncontrolled; content is read from the editor ref at
 * save time. Saves are debounced autosaves with an optimistic-concurrency check (one DB write),
 * Mod-S, an unload guard and an in-app navigation guard that saves before leaving. Lists in this
 * window update through AppContext.patchGeneratedDocumentLocal; an opener tab (when the document
 * was opened with "Open in new tab") is notified with DOCUMENT_UPDATED.
 */
const StandaloneEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const documentId = params.documentId || searchParams.get('documentId');
  const queryProjectId = params.projectId || searchParams.get('projectId') || null;
  const appActions = useAppActions();
  const {
    editorPageMode,
    setEditorPageMode,
    focusMode,
    setFocusMode,
    openInky,
  } = useWorkspaceLayout();
  // Editor chrome: the document header replaces the shell's top bar and sidebar here.
  useEditorChrome();

  const [documentData, setDocumentData] = useState(null);
  const [initialContent, setInitialContent] = useState('');
  const [loadVersion, setLoadVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [liveHtml, setLiveHtml] = useState('');
  const [toast, setToast] = useState(null);
  const [editorReady, setEditorReady] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  // Pages view paper size: a per-browser preference shared with the exporters.
  const [paperSize, setPaperSize] = useState(readPaperSize);
  useEffect(() => subscribePaperSize(setPaperSize), []);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [headerHeight, setHeaderHeight] = useState(HEADER_HEIGHT);
  const headerRef = useRef(null);
  const editorRef = useRef(null);
  const updatedAtRef = useRef(null);
  const forceNextSaveRef = useRef(false);
  /** Set when a forced ("Keep mine") write actually reached the server. */
  const forcedWriteDoneRef = useRef(false);
  const { registerEditor, unregisterEditor } = useActiveEditor();

  const deferredHtml = useDeferredValue(liveHtml);
  const sourceItems = useMemo(() => extractTrustComments(deferredHtml), [deferredHtml]);
  const title = documentData?.name || 'Untitled Document';
  const projectId = documentData?.project_id || queryProjectId;

  useEffect(() => {
    if (!editorReady) return undefined;
    registerEditor(editorRef);
    return () => unregisterEditor(editorRef);
  }, [editorReady, registerEditor, unregisterEditor]);

  useEffect(() => {
    if (documentData?.name) document.title = `${documentData.name} - Quill AI`;
  }, [documentData?.name]);

  const save = useCallback(async (content) => {
    const { updatedAt } = await saveDocumentContent({
      documentId,
      content,
      expectedUpdatedAt: updatedAtRef.current,
      force: forceNextSaveRef.current,
    });
    updatedAtRef.current = updatedAt;
    if (forceNextSaveRef.current) forcedWriteDoneRef.current = true;
    forceNextSaveRef.current = false;
    appActions.patchGeneratedDocumentLocal(documentId, { content, updatedAt });
    notifyOpenerDocumentUpdated({ documentId, updatedAt, content, title });
  }, [documentId, title, appActions]);

  const getContent = useCallback(() => {
    const html = editorRef.current?.getHTML?.();
    if (typeof html !== 'string' || isEmptyHtml(html)) return null;
    return html;
  }, []);

  const autosave = useAutosave({
    getContent,
    save,
    delay: 1500,
    enabled: Boolean(documentId) && !loading && !loadError,
    isConflictError: (error) => error instanceof DocumentConflictError,
  });
  const { setBaseline, markDirty, saveNow, clearConflict, isDirty } = autosave;

  /**
   * Fetch the document and load it into the editor.
   * @returns {Promise<void>}
   */
  const loadDocument = useCallback(async () => {
    if (!documentId) {
      setLoadError('No document ID provided');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const data = await documentService.getDocumentById(documentId);
      setDocumentData(data);
      recordRecentDocument({ id: data.id, name: data.name, projectId: data.project_id });
      if (!data.content || data.content.trim() === '') {
        setLoadError(emptyDocumentMessage(data));
        return;
      }
      updatedAtRef.current = data.updated_at || null;
      setInitialContent(data.content);
      setLiveHtml(data.content);
      setLoadVersion((v) => v + 1);
    } catch (error) {
      console.error('Error fetching document:', error);
      if (error?.code === 'PGRST116') forgetRecentDocument(documentId);
      setLoadError(`Failed to load document: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  // Baseline the autosave with the editor's normalized HTML once content is loaded.
  useEffect(() => {
    if (!editorReady || !loadVersion) return;
    const html = editorRef.current?.getHTML?.();
    if (typeof html === 'string') setBaseline(html);
  }, [editorReady, loadVersion, setBaseline]);

  const handleChange = useCallback((html) => setLiveHtml(html), []);

  const handleManualSave = useCallback(async () => {
    const html = editorRef.current?.getHTML?.();
    if (!html || isEmptyHtml(html)) {
      setToast({ severity: 'warning', message: 'Empty documents are not saved.' });
      return;
    }
    const ok = await saveNow();
    if (ok) setToast({ severity: 'success', message: 'Saved' });
  }, [saveNow]);

  /** Reload the server version, discarding local edits (conflict resolution). */
  const handleReloadFromServer = useCallback(async () => {
    clearConflict();
    await loadDocument();
  }, [clearConflict, loadDocument]);

  /**
   * Overwrite the server version with the local one (conflict resolution). Always performs the
   * forced write (even when the content equals the last saved version), never leaves the force
   * flag armed, and only confirms when a write actually happened.
   */
  const handleOverwrite = useCallback(async () => {
    forceNextSaveRef.current = true;
    forcedWriteDoneRef.current = false;
    clearConflict();
    try {
      const ok = await saveNow({ force: true });
      if (ok && forcedWriteDoneRef.current) setToast({ severity: 'success', message: 'Your version was saved.' });
    } finally {
      forceNextSaveRef.current = false;
      forcedWriteDoneRef.current = false;
    }
  }, [clearConflict, saveNow]);

  /**
   * Suggestions waiting for review (Inky's edits, Regenerate section) live only in the editor's
   * memory and are not part of the saved document, so leaving discards them silently.
   * @returns {{label?: string}|null} The pending review, or null.
   */
  const getPendingReview = useCallback(() => {
    try {
      const pending = editorRef.current?.getPendingReview?.();
      return pending && pending.changes?.length ? pending : null;
    } catch {
      return null;
    }
  }, []);

  // In-app navigation away (links, Inky's app.navigate, back): confirm discarding suggestions
  // that are still waiting for review, then save pending edits first.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => currentLocation.pathname !== nextLocation.pathname && (isDirty() || Boolean(getPendingReview()))
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    let cancelled = false;
    (async () => {
      const pending = getPendingReview();
      if (pending) {
        const whose = /inky/i.test(pending.label || '') ? "Inky's suggestions are" : 'Suggested changes are';
        if (!window.confirm(`${whose} still waiting for your review. Leave and discard them?`)) {
          blocker.reset();
          return;
        }
      }
      const saved = isDirty() ? await saveNow() : true;
      if (cancelled) return;
      if (saved || window.confirm('Some changes could not be saved. Leave anyway?')) blocker.proceed();
      else blocker.reset();
    })();
    return () => {
      cancelled = true;
    };
  }, [blocker, saveNow, isDirty, getPendingReview]);

  // Refresh or tab close with suggestions still waiting for review: ask the browser to confirm
  // (unsaved edits are covered by useAutosave's own guard).
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!getPendingReview()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [getPendingReview]);

  // Actions that unmount the page without a router navigation (logging out flips the shell
  // off) save pending edits through this hook first; the router blocker never sees them.
  useEffect(
    () => registerUnsavedWorkFlusher(async () => (isDirty() ? saveNow() : true)),
    [isDirty, saveNow]
  );

  // When this document runs in a tab opened from another Quill tab, tell the opener it closed
  // so the opener's list refreshes right away.
  useEffect(() => {
    const notifyClose = () => {
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({ type: 'DOCUMENT_EDITOR_CLOSE', data: { documentId } }, window.location.origin);
        }
      } catch {
        /* opener gone or cross-origin */
      }
    };
    window.addEventListener('pagehide', notifyClose);
    return () => window.removeEventListener('pagehide', notifyClose);
  }, [documentId]);

  const handleBack = useCallback(() => {
    navigate(projectId ? projectPath(projectId, 'documents') : '/documents');
  }, [navigate, projectId]);

  /**
   * "Regenerate with Inky" / "Fill this section with Inky" on a generated section: opens Inky
   * with a request PREFILLED (not sent) that asks for a fresh version from the project's
   * requirements and meetings and ends with "Guidance: ", plus one-click guidance chips. The
   * section's template instruction is added when the template still has it. Inky's edit comes
   * back as a review (accept/reject).
   * The section (or inline field) is selected in the editor first, so Inky edits exactly it;
   * the request is worded around that selection and never shows internal ids.
   * @param {{fieldId: string|null, heading: string, status: string|null, text: string, inline?: boolean, element?: HTMLElement}} info - Section (GeneratedSectionChrome).
   * @returns {Promise<void>}
   */
  const handleRegenerateSection = useCallback(async (info) => {
    const bridge = editorRef.current;
    if (bridge?.isDiffMode) {
      setToast({ severity: 'info', message: 'Accept or reject the open changes first.' });
      return;
    }
    const selection = bridge?.selectGeneratedSection?.({ fieldId: info?.fieldId, element: info?.element }) || { ok: false };
    const inline = selection.ok ? selection.inline : Boolean(info?.inline);
    const currentValue = selection.ok ? selection.text : info?.text || '';
    const templateId = documentData?.template_id || null;
    const field = info?.fieldId && templateId ? await loadTemplateField(templateId, info.fieldId) : null;
    const needs = /\[Needs input:\s*([^\]]+)\]/i.exec(currentValue || info?.text || '');
    openInky({
      prompt: buildRegeneratePrompt({
        heading: info?.heading,
        status: info?.status,
        needsInput: needs ? needs[1] : null,
        instruction: field?.instruction || null,
        inline,
        currentValue: inline ? currentValue : '',
      }),
      send: false,
      chips: REGENERATE_GUIDANCE_CHIPS,
    });
  }, [documentData, openInky]);

  /** "Ask Inky about this": the section selected and quoted in the composer, never sent. */
  const handleAskAboutSection = useCallback((info) => {
    editorRef.current?.selectGeneratedSection?.({ fieldId: info?.fieldId, element: info?.element });
    openInky({ prompt: buildAskAboutSectionPrompt({ heading: info?.heading, text: info?.text }) });
  }, [openInky]);

  const generatedSectionActions = useMemo(
    () => ({ onRegenerate: handleRegenerateSection, onAsk: handleAskAboutSection }),
    [handleRegenerateSection, handleAskAboutSection]
  );

  /** Sources popover: jump to and select a section. */
  const handleSelectSource = useCallback((item) => {
    const bridge = editorRef.current;
    if (!bridge) return;
    if (!bridge.selectTrustSection?.(item.sectionIndex)) bridge.scrollToTrustSection?.(item.sectionIndex);
  }, []);

  /** Sources popover: "Fix with Inky" for a section that needs a source (sent at once). */
  const handleFixSource = useCallback((item) => {
    editorRef.current?.selectTrustSection?.(item.sectionIndex);
    openInky({ prompt: buildSourceFixPrompt(item), send: true });
  }, [openInky]);

  // The header wraps on narrow screens; the toolbar sticks right below its real height.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const update = () => {
      const next = Math.round(el.getBoundingClientRect().height) || HEADER_HEIGHT;
      setHeaderHeight((prev) => (prev === next ? prev : next));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // The opener may ask this tab to save and go back.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'DOCUMENT_EDITOR_CLOSE_REQUEST') {
        saveNow().finally(handleBack);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleBack, saveNow]);

  // Mod-S anywhere on the page (the editor handles it while focused inside).
  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 's') {
        event.preventDefault();
        handleManualSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleManualSave]);

  /** "Ask Inky" from the editor's inline AI menu: quote the selection, never send. */
  const handleAskInChat = useCallback((text) => openInky({ prompt: quoteForInky(text) }), [openInky]);

  const handleOpenInNewTab = useCallback(async () => {
    setMenuAnchor(null);
    // Open first (still inside the click, so popup blockers allow it), then flush edits.
    openInNewTab(documentEditorPath(documentId, { projectId }));
    if (isDirty()) await saveNow();
  }, [documentId, projectId, isDirty, saveNow]);

  const handleCopyLink = useCallback(async () => {
    setMenuAnchor(null);
    const url = new URL(documentEditorPath(documentId, { projectId }), window.location.origin).toString();
    try {
      await navigator.clipboard.writeText(url);
      setToast({ severity: 'success', message: 'Link copied' });
    } catch {
      setToast({ severity: 'warning', message: url });
    }
  }, [documentId, projectId]);

  /**
   * Export the current editor content.
   * @param {'word'|'pdf'|'pptx'} type - Format.
   * @returns {Promise<void>}
   */
  const handleExport = useCallback(async (type) => {
    setMenuAnchor(null);
    setExportAnchor(null);
    const html = editorRef.current?.getHTML?.() || liveHtml;
    if (!html || isEmptyHtml(html)) {
      setToast({ severity: 'warning', message: 'There is nothing to export yet.' });
      return;
    }
    const base = fileBaseName(title);
    const projectName = (projectId && appActions.getProjectById(projectId)?.name) || '';
    setExporting(type);
    try {
      if (type === 'pptx') {
        const { default: pptxExportService } = await import('../services/pptxExportService');
        await pptxExportService.exportToPptx(html, `${base}.pptx`, projectName);
      } else {
        const { default: enhancedExportService } = await import('../services/enhancedExportService');
        if (type === 'word') await enhancedExportService.exportToWord(html, `${base}.docx`, projectName);
        else await enhancedExportService.exportToPDF(html, `${base}.pdf`, projectName);
      }
    } catch (error) {
      console.error(`${type} export failed:`, error);
      setToast({ severity: 'error', message: `Export failed: ${error?.message || 'unknown error'}` });
    } finally {
      setExporting(null);
    }
  }, [liveHtml, title, projectId, appActions]);

  const isDeck = isDeckDocument(documentData, liveHtml);
  const ready = !loading && !loadError;

  const header = (
    <Box
      component="header"
      ref={headerRef}
      sx={{
        position: 'sticky',
        top: 'var(--q-page-sticky-top, 0px)',
        zIndex: 20,
        minHeight: HEADER_HEIGHT,
        display: focusMode ? 'none' : 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: { xs: 0.5, sm: 1 },
        rowGap: 0.5,
        px: { xs: 0.5, sm: 1.5 },
        py: 0.75,
        bgcolor: 'var(--q-surface, #FFFFFF)',
        borderBottom: '1px solid var(--q-border, #E3E0DA)',
      }}
    >
      <EditorNavButton />
      <Tooltip title="Back to documents">
        <IconButton aria-label="Back to documents" onClick={handleBack}>
          <ArrowBack />
        </IconButton>
      </Tooltip>
      <Box sx={{ flex: '1 1 220px', minWidth: 180, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <EntityBreadcrumbs documentId={documentId} projectId={projectId} current={loading && !documentData ? 'Loading…' : title} sx={{ fontSize: 12.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography component="h1" noWrap title={title} sx={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em', minWidth: 0 }}>
            {loading && !documentData ? 'Loading…' : title}
          </Typography>
          {ready && (
            <SaveStatusChip status={autosave.status} error={autosave.error} lastSavedAt={autosave.lastSavedAt} halted={autosave.isHalted()} />
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, ml: 'auto', flex: 'none' }}>
        {ready && <DocumentSourcesChip items={sourceItems} onSelect={handleSelectSource} onFix={handleFixSource} />}
        <EditorJobsButton />
        {ready && <InkyReopenButton />}
        {ready && (
          <Button
            variant="contained"
            disableElevation
            aria-haspopup="menu"
            aria-expanded={exportAnchor ? 'true' : undefined}
            onClick={(event) => setExportAnchor(event.currentTarget)}
            disabled={Boolean(exporting)}
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlined />}
            endIcon={<ArrowDropDown />}
            aria-label="Export"
            sx={{ borderRadius: 999, flex: 'none', whiteSpace: 'nowrap', minWidth: 0, px: { xs: 1.25, sm: 2 }, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Export</Box>
          </Button>
        )}
        <Tooltip title="More">
          <IconButton
            aria-label="Document actions"
            aria-haspopup="menu"
            aria-expanded={menuAnchor ? 'true' : undefined}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            disabled={!documentId}
          >
            <MoreVert />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 210 } } }}
      >
        {isDeck ? (
          <MenuItem onClick={() => handleExport('pptx')} disabled={Boolean(exporting)}>
            <ListItemIcon><SlideshowOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>PowerPoint (.pptx)</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleExport('word')} disabled={Boolean(exporting)}>
            <ListItemIcon><DescriptionOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Word (.docx)</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleExport('pdf')} disabled={Boolean(exporting)}>
          <ListItemIcon><PictureAsPdfOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>PDF</ListItemText>
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        {ready && [
          ...[
            { mode: 'pages', label: 'Pages', icon: <ViewAgendaOutlined fontSize="small" /> },
            { mode: 'page', label: 'Page width', icon: <WidthNormal fontSize="small" /> },
            { mode: 'wide', label: 'Full width', icon: <WidthWide fontSize="small" /> },
          ].map(({ mode, label, icon }) => (
            <MenuItem
              key={mode}
              role="menuitemradio"
              aria-checked={editorPageMode === mode ? 'true' : 'false'}
              selected={editorPageMode === mode}
              onClick={() => {
                setMenuAnchor(null);
                setEditorPageMode(mode);
              }}
            >
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText>{label}</ListItemText>
              {editorPageMode === mode && <Check fontSize="small" aria-hidden="true" sx={{ ml: 2 }} />}
            </MenuItem>
          )),
          ...(editorPageMode === 'pages' ? [
            <Divider key="d-paper" />,
            // A separate radio set from the views above: its own heading, and names that say
            // what it sets (the heading itself is visual only).
            <ListSubheader key="paper-heading" aria-hidden="true" sx={{ lineHeight: '32px', bgcolor: 'transparent' }}>
              Paper size
            </ListSubheader>,
            ...PAPER_SIZE_IDS.map((id) => (
              <MenuItem
                key={`paper-${id}`}
                role="menuitemradio"
                aria-label={`${PAPER_SIZES[id].label} paper size`}
                aria-checked={paperSize === id ? 'true' : 'false'}
                selected={paperSize === id}
                onClick={() => {
                  setMenuAnchor(null);
                  setPaperSize(writePaperSize(id));
                }}
              >
                <ListItemIcon />
                <ListItemText>{PAPER_SIZES[id].label}</ListItemText>
                {paperSize === id && <Check fontSize="small" aria-hidden="true" sx={{ ml: 2 }} />}
              </MenuItem>
            )),
            <Divider key="d-paper-end" />,
          ] : []),
          <MenuItem
            key="focus"
            onClick={() => {
              setMenuAnchor(null);
              setFocusMode(true);
            }}
          >
            <ListItemIcon><CenterFocusStrongOutlined fontSize="small" /></ListItemIcon>
            <ListItemText secondary={formatShortcut('Mod+Shift+F')}>Focus mode</ListItemText>
          </MenuItem>,
          <Divider key="d1" />,
        ]}
        <MenuItem onClick={handleOpenInNewTab}>
          <ListItemIcon><OpenInNew fontSize="small" /></ListItemIcon>
          <ListItemText>Open in new tab</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
          <ListItemText>Copy link</ListItemText>
        </MenuItem>
        {ready && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              handleManualSave();
            }}
          >
            <ListItemIcon><SaveOutlined fontSize="small" /></ListItemIcon>
            <ListItemText secondary={formatShortcut('Mod+S')}>Save now</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );

  const editorElement = useMemo(() => (
    <EnhancedRichTextEditor
      ref={editorRef}
      documentKey={`${documentId}:${loadVersion}`}
      initialContent={initialContent}
      onDirty={markDirty}
      onChange={handleChange}
      onSave={handleManualSave}
      onReady={setEditorReady}
      meta={{ documentId, title }}
      placeholder="Start writing your document…"
      pageMode={editorPageMode}
      onPageModeChange={setEditorPageMode}
      paperSize={paperSize}
      onPaperSizeChange={setPaperSize}
      focusMode={focusMode}
      onRequestFocusMode={setFocusMode}
      onAskInChat={handleAskInChat}
      generatedSectionActions={generatedSectionActions}
      enableAdvancedFormatting
      enableImages
    />
  ), [
    documentId,
    loadVersion,
    initialContent,
    markDirty,
    handleChange,
    handleManualSave,
    title,
    editorPageMode,
    setEditorPageMode,
    paperSize,
    focusMode,
    setFocusMode,
    handleAskInChat,
    generatedSectionActions,
  ]);

  const pageSx = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    bgcolor: 'var(--q-canvas, #EEECE7)',
    '--q-editor-sticky-top': focusMode ? 'var(--q-page-sticky-top, 0px)' : `calc(var(--q-page-sticky-top, 0px) + ${headerHeight}px)`,
  };

  if (loading && !documentData) {
    return (
      <Box data-page-height="fill" sx={pageSx}>
        {header}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4 }}>
          <CircularProgress size={32} />
          <Typography color="text.secondary">Loading document…</Typography>
        </Box>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box data-page-height="fill" sx={pageSx}>
        {header}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4 }}>
          <Alert severity="error" sx={{ maxWidth: 520 }}>{loadError}</Alert>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={loadDocument}>Retry loading</Button>
            <Button onClick={handleBack}>Back to documents</Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box data-page-height="fill" sx={pageSx}>
      {header}

      {autosave.status === 'conflict' && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 0 }}
          action={(
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button color="inherit" size="small" onClick={handleReloadFromServer}>Reload</Button>
              <Button color="inherit" size="small" variant="outlined" onClick={handleOverwrite}>Keep mine</Button>
            </Box>
          )}
        >
          This document was changed in another tab or by a teammate. Reload their version (your unsaved edits are discarded) or keep yours and overwrite.
        </Alert>
      )}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          px: { xs: 1, sm: 1.5 },
          pt: focusMode ? 6 : 1,
          pb: 4,
        }}
      >
        {/* Sources live in the header chip, so the page stays centred with no side column. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>{editorElement}</Box>
      </Box>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={toast?.severity === 'success' ? 1800 : 5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert onClose={() => setToast(null)} severity={toast.severity}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default StandaloneEditor;
